import { CategoryApi } from '@common/api/category';
import { useBreakpoint } from '@common/application/hooks/use-breakpoint';
import { pageHomeCategory } from '@common/application/store/category';
import { classNames } from '@common/application/utils/classnames';
import { createPath } from '@common/application/utils/create-path';
import { emitCommonCategory } from '@common/application/utils/event';
import { DImage } from '@common/components/common/image';
import { ROUTE_MAP } from '@common/constants/route-map';
import type { ICategory } from '@common/types/category';
import type { NoticeType } from '@common/types/common';
import { useDebounceFn, useMemoizedFn, useToggle } from 'ahooks';
import { isEmpty } from 'lodash-es';
import Router from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { BlackLineIcon } from '@/resources/sprites-images/black-line-icon';

import { GoodsCategory } from '../goods-category';

export const CategoryContext = React.createContext<{
  onHide: (callback?: () => void) => void;
  categoryClick: (data: ICategory, categoryPath: string | string[]) => void;
  firstCategory: ICategory | undefined;
  activeCategory: ICategory | undefined;
} | null>(null);

export const CategoryList: React.FC<{ noticeMsgList: NoticeType[] }> = ({ noticeMsgList }) => {
  const { categoryList: categoryData } = useRecoilValue(pageHomeCategory);
  const setPageHomeCategory = useSetRecoilState(pageHomeCategory);
  const screens = useBreakpoint();

  const [firstCategory, setFirstData] = useState<ICategory>();
  const [activeCategory, setActiveCategory] = useState<ICategory>();

  const [open, openActions] = useToggle();
  const timer = useRef<NodeJS.Timer | null>(null);

  const triggerCategotyEl = useRef<HTMLDivElement | null>(null);
  const [categoryTop, setCategoryTop] = useState<number>(0);

  const getCategoryBrand = async (item: ICategory) => {
    try {
      const res = await CategoryApi.getRecommendBrandLiving(item.id, { hideLoading: true });
      if (!isEmpty(res)) {
        setPageHomeCategory((value) => ({ ...value, recommendBrandList: res }));
      } else {
        setPageHomeCategory((value) => ({ ...value, recommendBrandList: [] }));
      }
    } catch (error) {
      setPageHomeCategory((value) => ({ ...value, recommendBrandList: [] }));
    }
  };

  const clickCategory = (item: ICategory) => {
    if (timer.current) clearTimeout(timer.current);
    setCategoryTop(triggerCategotyEl.current?.getBoundingClientRect().bottom || 0);
    setFirstData(item);
    if (!isEmpty(item.childTree)) {
      openActions.set(true);
      getCategoryBrand(item);
      /**
       * moblie端点击分类标识，滚动加载需要
       */
      emitCommonCategory(false);
      if (open && activeCategory?.id === item.id && screens.mobile) {
        openActions.set(false);
        setActiveCategory(undefined);
        /**
         * moblie端点击分类标识，滚动加载需要
         */
        emitCommonCategory(true);
        return;
      }
    } else {
      openActions.set(false);
    }
    setActiveCategory(item);
  };

  const { run } = useDebounceFn(clickCategory, { wait: 500, leading: true });

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const closeCategory = (callback?: () => void) => {
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      openActions.set(false);
      setActiveCategory(undefined);
      if (callback) {
        callback();
      }
    }, 200);
  };

  const categoryClick = (data: ICategory, categoryPath: string | string[]) => {
    if (screens.mobile && data.childTree.length) return;
    closeCategory();
    Router.push(
      createPath(ROUTE_MAP.CATEGORY_LIST, {
        category: categoryPath,
        id: data.id
      })
    );
  };

  const otherPropsFn = useMemoizedFn((item: ICategory) => {
    return screens.pc
      ? {
          onMouseEnter: () => {
            run(item);
          }
        }
      : {
          onClick: () => {
            run(item);
          }
        };
  });

  return (
    <CategoryContext.Provider
      value={{
        onHide: (callback?: () => void) => closeCategory(callback),
        categoryClick,
        firstCategory,
        activeCategory
      }}
    >
      <div
        className={classNames(
          'grid grid-flow-col h-full justify-self-start max-w-full navigation-scrollbar'
        )}
        onMouseLeave={() => closeCategory()}
      >
        {categoryData.map((item, index) => (
          <div
            ref={triggerCategotyEl}
            key={item.id}
            className={classNames(
              'relative flex items-center border-none pad:border-t border-rose-700 cursor-pointer first:border-none',
              'text-center px-3.25 h-11',
              'pc:text-left pc:px-5 pc:h-12',
              {
                'bg-white': activeCategory?.id === item.id,
                'pl-0 pc:pl-0': index === 0,
                'pr-3.25 pc:pr-5': index === categoryData.length - 1 && !isEmpty(noticeMsgList),
                'pr-0 pc:pr-0': index === categoryData.length - 1 && isEmpty(noticeMsgList)
              }
            )}
            style={{ width: `calc(100%)` }}
            {...otherPropsFn(item)}
            data-testid={`test-category-${index}`}
          >
            <a
              href={createPath(
                ROUTE_MAP.CATEGORY_LIST,
                {
                  category: item.name,
                  id: item.id
                },
                { withLocale: true }
              )}
              className={classNames(
                'items-center justify-center line-clamp-2 cursor-pointer text-black-2 flex font-medium',
                'text-1xs',
                'pc:text-sm',
                ...(activeCategory?.id === item.id ? ['text-primary pad:text-primary'] : []),
                { 'hover:text-primary': screens.pc }
              )}
              onClick={(e) => {
                e.preventDefault();
                categoryClick(item, item.name);
              }}
            >
              <DImage
                src={screens.pc ? item.pcPicturePath : item.picturePath}
                className={classNames('flex-shrink-0', 'w-5 h-5', 'pc:w-6 pc:h-6')}
                fill
              />
              <div className={classNames('whitespace-nowrap', 'mx-0.5', 'pc:mx-1')}>
                {item.name}
              </div>
              <BlackLineIcon
                className="w-4 h-4"
                icon={activeCategory?.id === item.id ? 'top-arrow' : 'bottom-arrow'}
              />
            </a>
          </div>
        ))}
        <GoodsCategory open={open} top={categoryTop} data={activeCategory} />
      </div>
    </CategoryContext.Provider>
  );
};
